import { useState } from 'react';
import { Phone, Mail, MapPin, ChevronDown, User, X, ZoomIn, ZoomOut } from 'lucide-react';
import { Campaign } from '../../hooks/useCampaignDetail';
import { OwnerProfile } from '../../hooks/useCampaignDetail';

interface Props {
  campaign: Campaign;
  ownerProfile: OwnerProfile | null;
}

export function ContactInfo({ campaign, ownerProfile }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [zoom, setZoom] = useState(1);

  return (
    <>
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Clickable header */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {/* Owner avatar */}
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
              {ownerProfile?.profileImageUrl ? (
                <img src={ownerProfile.profileImageUrl} alt={ownerProfile.fullName} className="w-full h-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-gray-400" />
              )}
            </div>
            <div className="text-left">
              <span className="text-sm font-semibold text-gray-900 block">
                Información de quien recauda
              </span>
              <span className="text-xs text-gray-500">
                {ownerProfile?.fullName || 'Organizador'}
              </span>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Collapsible content */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${
            isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
            {/* Profile card */}
            {ownerProfile && (
              <div className="flex items-start space-x-3 pb-4 border-b border-gray-100">
                {ownerProfile.profileImageUrl ? (
                  <button
                    onClick={() => setShowImageModal(true)}
                    className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 hover:ring-2 hover:ring-indigo-300 transition-all cursor-zoom-in"
                  >
                    <img src={ownerProfile.profileImageUrl} alt={ownerProfile.fullName} className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-7 h-7 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{ownerProfile.fullName}</p>
                  {ownerProfile.title && (
                    <p className="text-xs text-indigo-600 font-medium">{ownerProfile.title}</p>
                  )}
                  {ownerProfile.bio && (
                    <p className="text-xs text-gray-500 mt-1">{ownerProfile.bio}</p>
                  )}
                </div>
              </div>
            )}

            {/* Contact details */}
            {campaign.contactInfo?.phone && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-medium">{campaign.contactInfo.phone}</p>
                  <div className="flex items-center space-x-3 mt-0.5">
                    <a href={`tel:${campaign.contactInfo.phone}`} className="text-xs text-indigo-600 hover:underline">Llamar</a>
                    <a
                      href={`https://wa.me/${campaign.contactInfo.phone.replace(/\D/g, '').replace(/^(?!57)/, '57')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-green-600 hover:underline"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            )}

            {campaign.contactInfo?.email && (
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-medium break-all">{campaign.contactInfo.email}</p>
                  <a href={`mailto:${campaign.contactInfo.email}`} className="text-xs text-indigo-600 hover:underline">
                    Enviar email
                  </a>
                </div>
              </div>
            )}

            {campaign.location?.address && (
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-900 font-medium">{campaign.location.address}</p>
                  <p className="text-xs text-gray-500">{campaign.location.city}, {campaign.location.state}</p>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(campaign.location.address + ', ' + campaign.location.city)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-indigo-600 hover:underline mt-0.5 inline-block"
                  >
                    Ver en mapa
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image zoom modal */}
      {showImageModal && ownerProfile?.profileImageUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            {/* Controls */}
            <div className="absolute top-4 right-4 flex items-center space-x-2 z-10">
              <button
                onClick={() => setZoom(z => Math.min(z + 0.5, 3))}
                className="p-2 bg-white/90 rounded-full text-gray-700 hover:bg-white transition-colors shadow"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              <button
                onClick={() => setZoom(z => Math.max(z - 0.5, 1))}
                className="p-2 bg-white/90 rounded-full text-gray-700 hover:bg-white transition-colors shadow"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <button
                onClick={() => { setShowImageModal(false); setZoom(1); }}
                className="p-2 bg-white/90 rounded-full text-gray-700 hover:bg-white transition-colors shadow"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Image */}
            <div className="overflow-auto rounded-xl max-h-[80vh]">
              <img
                src={ownerProfile.profileImageUrl}
                alt={ownerProfile.fullName}
                className="w-full rounded-xl transition-transform duration-200"
                style={{ transform: `scale(${zoom})`, transformOrigin: 'center' }}
              />
            </div>

            {/* Name */}
            <div className="text-center mt-3">
              <p className="text-white font-medium">{ownerProfile.fullName}</p>
              {ownerProfile.title && <p className="text-white/70 text-sm">{ownerProfile.title}</p>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
